import React from 'react';
import styled from 'styled-components';
import { Col, Divider, Row, Tooltip } from 'tdesign-react';
import { HelpCircleIcon } from 'tdesign-icons-react';
import { useNavigate } from 'react-router-dom';
import ShadowCard from '@/components/ShadowCard';
import HealthGauge from '@/components/HealthGauge';
import useHealthColor from '@/hooks/useHealthColor';
import ClusterBadgeTag from '@/pages/Cluster/components/ClusterBadgeTag';
import FooterItem from './FooterItem';
import bgPng from './bg.png';

const backupServerColor = '#3361ff';

export default function ListItem({
  title,
  displayName,
  version,
  node,
  isOpenFunctionService,
  chartPercent = 0,
  items = [],
  tags = [],
  createTime,
  alive,
  isDisasterRecoveryCluster,
  zkCount,
  activeZkCount,
  bookiesCount,
  activeBookiesCount,
  brokersCount,
  activeBrokersCount,
  enableDisasterRecovery,
  ...others
}) {
  const navigate = useNavigate();

  // 节点颜色，跟随机器状态
  const [nodeColor] = useHealthColor(chartPercent);

  const goDetail = () => {
    navigate(`/cluster/dashboard?name=${title}`);
  };

  return (
    <Root $isBackupServer={isDisasterRecoveryCluster} onClick={goDetail} {...others}>
      <Container $alive={alive}>
        <Row>
          <Col flex='90px'>
            <ChartContainer>
              <Tooltip
                theme='light'
                showArrow
                destroyOnClose
                content={
                  <>
                    集群的健康分数依据存活的节点计算
                    <br />
                    broker节点<B>{brokersCount}</B>个，在线
                    <B style={{ color: nodeColor }}>{activeBrokersCount}</B>个
                    <br />
                    bookie节点<B>{bookiesCount}</B>个，在线
                    <B style={{ color: nodeColor }}>{activeBookiesCount}</B>个
                    <br />
                    zk节点<B>{zkCount}</B>个，在线
                    <B style={{ color: nodeColor }}>{activeZkCount}</B>个
                  </>
                }
              >
                <div>
                  <HealthGauge percent={chartPercent} />
                </div>
              </Tooltip>
              <Node title={node} className='node' color={nodeColor}>
                {node}
              </Node>
            </ChartContainer>
          </Col>
          <Col flex={1} style={{ paddingLeft: 40 }}>
            <Title className='title'>{displayName}</Title>
            <SubTitleLine>
              <SubTitle>版本：{version}</SubTitle>
              <Divider layout='vertical' />
              <SubTitle>{isOpenFunctionService ? '开启' : '关闭'}Function</SubTitle>
              {/* <Divider layout='vertical' />
              <SubTitle>{isOpenKopService ? '开启' : '关闭'}KOP</SubTitle> */}
            </SubTitleLine>
            <TagsContainer>
              {tags.map((tag, index) => (
                <Tag style={{ background: tag.background, color: tag.color }} key={index}>
                  {tag.title}
                </Tag>
              ))}
            </TagsContainer>
          </Col>
          <Col flex='158px'>
            {createTime && (
              <Tooltip theme='light' showArrow destroyOnClose content='集群创建/接入时间'>
                <TimeInfo>
                  {createTime}&nbsp;
                  <HelpCircleIcon />
                </TimeInfo>
              </Tooltip>
            )}
          </Col>
        </Row>
        <Footer>
          {items.map((item, index) => (
            <ItemContainer key={index}>
              <FooterItem {...item} />
            </ItemContainer>
          ))}
        </Footer>
      </Container>
      {!alive && <ClusterBadgeTag color='rgb(238 68 68 / 50%)'>离线</ClusterBadgeTag>}
      {enableDisasterRecovery && (
        <ClusterBadgeTag color='rgba(16, 184, 128)' size='small'>
          <Tooltip placement='left' content='已绑备集群'>
            主
          </Tooltip>
        </ClusterBadgeTag>
      )}
      {isDisasterRecoveryCluster && (
        <ClusterBadgeTag color={backupServerColor} size='small'>
          <Tooltip
            placement='left'
            content='备集群通常用于提供高可用性、容错性和数据备份等功能，以应对故障和灾难的场景。通常建议该集群单一职责，不与业务场景混用。'
          >
            备
          </Tooltip>
        </ClusterBadgeTag>
      )}
      {isDisasterRecoveryCluster && <Bg src={bgPng} />}
    </Root>
  );
}

const Root = styled(ShadowCard)`
  min-width: 720px;
  margin-bottom: 16px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  ${(props) =>
    props.$isBackupServer &&
    `
      background: rgb(123 143 211 / 8%);
    `}
`;

const Bg = styled.img`
  position: absolute;
  width: 340px;
  height: 284px;
  right: -40px;
  bottom: -74px;
  pointer-events: none;
`;

const Container = styled.div`
  ${(props) =>
    !props.$alive &&
    `
    filter:grayscale(100%);
    .title, .footer-info {
      color: var(--td-text-color-secondary);
    }
    .node {
      opacity: .5;
    }
  `}
`;

const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Node = styled.div`
  padding: 0 12px;
  box-sizing: border-box;
  background: ${(props) => props.color};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.05);
  border-radius: 6px;
  font-weight: 500;
  font-size: 13px;
  color: #ffffff;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 90px;
`;

const Title = styled.div`
  font-weight: bold;
  font-size: 20px;
  line-height: 28px;
  color: #0d111a;
  margin-bottom: 6px;
  cursor: pointer;
`;

const SubTitleLine = styled.div`
  display: flex;
  align-items: center;
`;

const SubTitle = styled.div`
  font-size: 14px;
  line-height: 20px;
  color: rgba(32, 43, 64, 0.5);
`;

const Footer = styled.div`
  margin-right: 20px;
  padding-top: 20px;
  padding-left: 14px;
  display: flex;
`;

const ItemContainer = styled.div`
  flex: 1;
`;

const TagsContainer = styled.div`
  display: flex;
  margin-top: 10px;
`;

const Tag = styled.div`
  margin-right: 8px;
  background: rgba(125, 133, 146, 0.14);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  line-height: 18px;
  color: #7d8592;
  padding: 2px 10px;
`;

const TimeInfo = styled.div`
  font-size: 12px;
  line-height: 12px;
  color: rgba(32, 43, 64, 0.5);
  display: flex;
  align-items: center;
  padding-right: 8px;
`;

const B = styled.span`
  font-weight: bold;
  padding: 0 4px;
`;
